import React, {useCallback, useEffect, useState} from 'react';
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Tag} from "primereact/tag";

import {AppContext} from "../../HomePage";
import {ScraperService} from "../../service/ScraperService";
import {job_states} from "../../utils/Constants";
import {Loading} from "../../utils/InlineComponents";

const scraperService = new ScraperService();

const stateSeverity = (state) => {
    if (state === job_states.done) return 'success';
    if (state === job_states.failed) return 'danger';
    if (state === job_states.in_progress || state === 'running') return 'warning';
    if (state === job_states.in_preparation) return 'info';
    return null;
};

// Reusable "Job State" table: recent jobs for a campaign, filtered by type(s).
// Each row is enriched with its live status (state, items, errors, ...).
const CampaignJobsState = ({campaignId, types = [], days = 2, title, reloadToken}) => {
    const {t, setError} = React.useContext(AppContext);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState(null);
    const [showDetail, setShowDetail] = useState(false);

    const typesKey = (types || []).join(',');

    const load = useCallback(() => {
        if (!campaignId) return;
        setLoading(true);
        scraperService.getCampaignJobsState(campaignId, types, days).then(list => {
            const rows = list || [];
            if (rows.length === 0) {
                setJobs([]);
                setLoading(false);
                return;
            }
            let remaining = rows.length;
            rows.forEach(jobInfo => {
                const id = jobInfo.spider_args && jobInfo.spider_args.scraping_request_id;
                scraperService.getScrapingRequestStatus(id, jobInfo.job_key).then(data => {
                    jobInfo.spider_args.divided_job_no = data.spider_args && data.spider_args.divided_job_no;
                    jobInfo.spider_args.divided_job_units = data.spider_args && data.spider_args.divided_job_units;
                    jobInfo.close_reason = data.close_reason;
                    jobInfo.items_scraped = data.items_scraped;
                    jobInfo.state = data.state;
                    jobInfo.spider_name = data.spider_name;
                    jobInfo.started_time = data.started_time;
                    jobInfo.updated_time = data.updated_time;
                    jobInfo.errors_count = data.errors_count;
                }).catch(() => {}).finally(() => {
                    remaining -= 1;
                    if (remaining === 0) {
                        rows.sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0));
                        setJobs([...rows]);
                        setLoading(false);
                    }
                });
            });
        }).catch(error => {
            setError(error);
            setError(null);
            setLoading(false);
        });
    }, [campaignId, typesKey, days]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load, reloadToken]);

    const dividedJobTemplate = (row) => {
        const no = row.spider_args && row.spider_args.divided_job_no;
        const units = row.spider_args && row.spider_args.divided_job_units;
        const label = no !== undefined && no !== null ? (parseInt(no) + 1) + "/" + units : "1/1";
        return <a target="_blank" rel="noopener noreferrer" href={row.url}>{label}</a>;
    };
    const requestIdTemplate = (row) => row.spider_args && row.spider_args.scraping_request_id;
    const stateTemplate = (row) => row.state
        ? <Tag value={row.state} severity={stateSeverity(row.state) || undefined}/>
        : null;
    const actionTemplate = (row) => <Button icon="pi pi-info" className="p-button-outlined p-button-info"
                                            onClick={() => openDetail(row)}/>;

    const openDetail = (row) => {
        const id = row.spider_args && row.spider_args.scraping_request_id;
        scraperService.getScrapingRequest(id).then(data => {
            setDetail(data);
            setShowDetail(true);
        }).catch(error => {
            setError(error);
            setError(null);
        });
    };

    return (
        <div>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8}}>
                <b>{title || t('scraping_request.job_state')}</b>
                <Button icon="pi pi-refresh" className="p-button-text p-button-sm" loading={loading}
                        label={t('actions.refresh', 'Yenile')} onClick={load}/>
            </div>
            {loading
                ? <div style={{textAlign: 'center', padding: 16}}><Loading/></div>
                : <DataTable value={jobs} responsiveLayout="scroll" paginator={jobs.length > 10} rows={10}
                             emptyMessage={t('scraping_request.no_jobs', 'No records found.')}>
                    <Column body={dividedJobTemplate} header={t('scraping_request.divided_job_no')}/>
                    <Column body={requestIdTemplate} header={t('scraping_request.id')}/>
                    <Column field="spider_name" header={t('scraping_request.spider_name')}/>
                    <Column field="type" header={t('scraping_request.type')}/>
                    <Column field="close_reason" header={t('scraping_request.close_reason')}/>
                    <Column body={stateTemplate} header={t('scraping_request.state')}/>
                    <Column field="started_time" header={t('scraping_request.started_at')}/>
                    <Column field="updated_time" header={t('scraping_request.updated_time')}/>
                    <Column field="items_scraped" header={t('scraping_request.items_scraped')}/>
                    <Column field="errors_count" header={t('scraping_request.errors_count')}/>
                    <Column body={actionTemplate} style={{width: '5em', textAlign: 'center'}}/>
                </DataTable>}

            <Dialog header={t('scraping_request.job_detail')} visible={showDetail}
                    style={{width: '60vw', minWidth: '20vw'}} modal onHide={() => setShowDetail(false)}>
                {detail && <pre style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '.8rem'}}>
                    {JSON.stringify(detail, null, 2)}
                </pre>}
            </Dialog>
        </div>
    );
};

export default CampaignJobsState;
